import { type SwitchProps as BaseSwitchProps, default as BaseToggle } from '@mui/joy/Switch';
import { clsx } from 'clsx';
import noop from 'lodash/noop';
import { forwardRef, useMemo } from 'react';
import { Typography } from '../Typography';
import {
  getLabeledScale,
  getToggleLabelTextClassName,
  toggleActionVariantsReset,
  toggleRootVariantsReset,
  toggleThumbVariants,
  toggleTrackVariants,
} from './styles';
import type { ToggleProps } from './types';

const Toggle = forwardRef<HTMLInputElement, ToggleProps>((props, ref) => {
  const {
    disabled: disabledProp,
    readOnly: readOnlyProp,
    onChange,
    onClick,
    className,
    trackClassName,
    size = 'sm',
    color = 'brand',
    showLabel = false,
    onLabel,
    offLabel,
    customVariantDataAttributes,
    ...restProps
  } = props;

  const labelTextClassName = useMemo(() => getToggleLabelTextClassName(size), [size]);

  const labeledScale = useMemo(() => (showLabel ? getLabeledScale(size) : null), [showLabel, size]);

  const toggleSlotProps = useMemo<BaseSwitchProps['slotProps']>(
    () => ({
      root: ({ checked, disabled, focusVisible }) => ({
        className: toggleRootVariantsReset({
          className,
          checked: Boolean(checked),
          disabled: Boolean(disabled),
          focusVisible,
        }),
      }),
      track: ({ checked, disabled, focusVisible }) => ({
        className: toggleTrackVariants({
          className: clsx('ua-toggle', trackClassName),
          checked: Boolean(checked),
          disabled: Boolean(disabled),
          focusVisible,
          color,
        }),
        // Track width/height intentionally NOT set inline here — Joy's own styles
        // read `width: var(--Switch-trackWidth)` / `height: var(--Switch-trackHeight)`.
        // The root `sx` below seeds `--Switch-trackWidth` from the JS-computed value;
        // a theme-manager Layout customisation overrides the same var (token) and the
        // thumb-position calc automatically stays consistent.
        ...customVariantDataAttributes,
        'data-color': color,
        'data-state': checked ? 'checked' : 'unchecked',
        'data-size': size,
        'data-disabled': disabled ? 'true' : undefined,
        'data-readonly': readOnlyProp ? 'true' : undefined,
        'data-focus-visible': focusVisible ? 'true' : undefined,
        children: showLabel ? (
          <Typography
            aria-hidden
            className={clsx(
              'pointer-events-none absolute inset-y-0 z-badge flex select-none items-center justify-center whitespace-nowrap leading-none',
              labelTextClassName,
            )}
            color="text-white"
            data-slot="label"
            style={{
              insetInlineStart: checked ? 0 : 'var(--Switch-thumbSize)',
              insetInlineEnd: checked ? 'var(--Switch-thumbSize)' : 0,
            }}
            weight="medium"
          >
            {checked ? onLabel : offLabel}
          </Typography>
        ) : undefined,
      }),
      thumb: ({ checked, disabled, focusVisible }) => ({
        className: toggleThumbVariants({ checked, disabled, focusVisible }),
        'data-slot': 'thumb',
      }),
      action: ({ checked, disabled, focusVisible }) => ({
        className: toggleActionVariantsReset({ checked, disabled, focusVisible }),
        'data-slot': 'action',
      }),
      input: ({ disabled }) => ({
        ref,
        className: disabled ? '!cursor-not-allowed [&_*]:!cursor-not-allowed' : undefined,
        ...(readOnlyProp ? { 'aria-readonly': 'true' } : {}),
      }),
    }),
    [
      className,
      color,
      labelTextClassName,
      offLabel,
      onLabel,
      readOnlyProp,
      ref,
      trackClassName,
      size,
      showLabel,
      customVariantDataAttributes,
    ],
  );

  return (
    <BaseToggle
      disabled={disabledProp}
      onChange={readOnlyProp ? noop : onChange}
      onClick={onClick}
      readOnly={readOnlyProp}
      // @ts-expect-error xs does not exist on Joy UI's SwitchProps. and we have not overridden the type
      size={size}
      slotProps={toggleSlotProps}
      sx={
        labeledScale
          ? {
              '--Switch-trackWidth': `${labeledScale.width}px`,
              '--Switch-trackHeight': `${labeledScale.height}px`,
              '--Switch-thumbSize': `${labeledScale.thumb}px`,
              '--Switch-thumbWidth': `${labeledScale.thumb}px`,
            }
          : undefined
      }
      {...restProps}
    />
  );
});

Toggle.displayName = 'Toggle';

export default Toggle;
